import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Mic2, Video, ExternalLink, Users, Calendar, Play, Clock, Eye } from 'lucide-react';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';



export default function PodcasterPage() {
  const { podcasterId } = useParams();
  const [podcaster, setPodcaster] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPodcaster();
  }, [podcasterId]);

  const fetchPodcaster = async () => {
    try {
      const [podRes, interviewsRes] = await Promise.all([
        fetch(`${API}/podcasters/${podcasterId}`),
        fetch(`${API}/interviews?host_id=${podcasterId}`)
      ]);
      
      if (podRes.ok) {
        setPodcaster(await podRes.json());
      } else {
        setError('Podcaster not found');
      }
      
      if (interviewsRes.ok) {
        setInterviews(await interviewsRes.json());
      }
    } catch (err) {
      setError('Failed to load podcaster');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error || !podcaster) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Card className="glass-card p-8 text-center max-w-md">
          <Mic2 className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h2 className="font-heading text-2xl font-bold mb-2">Podcaster Not Found</h2>
          <p className="text-muted-foreground mb-4">{error}</p>
          <Link to="/creatives">
            <Button>Back to Creatives</Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4 sm:px-6 lg:px-8" data-testid="podcaster-page">
      <div className="max-w-6xl mx-auto">
        {/* Hero Section */}
        <div className="relative mb-8">
          {/* Banner */}
          <div className="h-48 sm:h-64 rounded-xl bg-gradient-to-br from-green-900/50 to-emerald-900/50 overflow-hidden">
            {podcaster.banner_url && (
              <img src={podcaster.banner_url} alt="" className="w-full h-full object-cover" />
            )}
          </div>
          
          {/* Profile Info */}
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 -mt-16 sm:-mt-20 px-4 sm:px-6">
            <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-xl bg-green-500/20 border-4 border-background flex items-center justify-center overflow-hidden">
              {podcaster.avatar_url ? (
                <img src={podcaster.avatar_url} alt={podcaster.show_name} className="w-full h-full object-cover" />
              ) : (
                <Mic2 className="w-16 h-16 text-green-400" />
              )}
            </div>
            <div className="flex-1 pt-4">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <h1 className="font-heading text-3xl sm:text-4xl font-bold" data-testid="podcaster-name">
                  {podcaster.show_name}
                </h1>
                {podcaster.is_verified && (
                  <Badge className="bg-green-500/20 text-green-400 border-green-500/30">Verified</Badge>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground mb-4">
                <Badge variant="outline">{podcaster.category}</Badge>
                <span className="flex items-center gap-1">
                  <Video className="w-4 h-4" /> {podcaster.episodes_count || 0} episodes
                </span>
                <span className="flex items-center gap-1">
                  <Users className="w-4 h-4" /> {podcaster.subscribers_count || 0} subscribers
                </span>
              </div>
              {podcaster.booking_link && (
                <a href={podcaster.booking_link} target="_blank" rel="noopener noreferrer">
                  <Button className="bg-green-600 hover:bg-green-700">
                    <Calendar className="w-4 h-4 mr-2" /> Book for Interview
                  </Button>
                </a>
              )}
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <Tabs defaultValue="interviews">
              <TabsList className="bg-muted/50 mb-6">
                <TabsTrigger value="interviews">Interviews</TabsTrigger>
                <TabsTrigger value="about">About</TabsTrigger>
              </TabsList>

              <TabsContent value="interviews">
                {interviews.length > 0 ? (
                  <div className="space-y-4">
                    {interviews.map((interview) => (
                      <Link key={interview.interview_id} to={`/interviews/${interview.interview_id}`}>
                        <Card className="glass-card border-border hover:border-border transition-all p-4">
                          <div className="flex gap-4">
                            <div className="w-32 h-20 rounded bg-muted/30 flex items-center justify-center flex-shrink-0">
                              {interview.cover_url ? (
                                <img src={interview.cover_url} alt="" className="w-full h-full object-cover rounded" />
                              ) : (
                                <Video className="w-8 h-8 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1">
                              <h3 className="font-bold line-clamp-1">{interview.title}</h3>
                              <p className="text-sm text-muted-foreground">Guest: {interview.guest_name}</p>
                              <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                                <span className="flex items-center gap-1">
                                  <Eye className="w-3 h-3" /> {interview.plays || 0}
                                </span>
                                {interview.duration && (
                                  <span className="flex items-center gap-1">
                                    <Clock className="w-3 h-3" /> {interview.duration}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </Card>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Card className="glass-card border-border p-8 text-center">
                    <Video className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                    <p className="text-muted-foreground">No interviews yet</p>
                  </Card>
                )}
              </TabsContent>

              <TabsContent value="about">
                <Card className="glass-card border-border p-6">
                  <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Bio</h3>
                  <p className="text-muted-foreground whitespace-pre-wrap">
                    {podcaster.bio || 'No bio provided yet.'}
                  </p>
                </Card>
              </TabsContent>
            </Tabs>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Stats */}
            <Card className="glass-card border-border p-4">
              <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Stats</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Plays</span>
                  <span className="font-bold">{podcaster.total_plays || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Episodes</span>
                  <span className="font-bold">{podcaster.episodes_count || 0}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subscribers</span>
                  <span className="font-bold">{podcaster.subscribers_count || 0}</span>
                </div>
              </div>
            </Card>

            {/* Socials */}
            {podcaster.socials && Object.keys(podcaster.socials).some(k => podcaster.socials[k]) && (
              <Card className="glass-card border-border p-4">
                <h3 className="font-heading font-bold uppercase text-sm text-muted-foreground mb-3">Links</h3>
                <div className="space-y-2">
                  {Object.entries(podcaster.socials).map(([platform, url]) => url && (
                    <a key={platform} href={url} target="_blank" rel="noopener noreferrer" className="block">
                      <Button variant="outline" size="sm" className="w-full justify-start">
                        <ExternalLink className="w-3 h-3 mr-2" />
                        {platform.charAt(0).toUpperCase() + platform.slice(1)}
                      </Button>
                    </a>
                  ))}
                </div>
              </Card>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
